import React, { Component } from "react";
import moment from "moment";
// material ui
import withStyles from "@material-ui/styles/withStyles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
// charts
import ReactEchartsCore from "echarts-for-react/lib/core";
import echarts from "echarts/lib/echarts";
import "echarts/lib/chart/bar";
import "echarts/lib/component/legend";
import "echarts/lib/component/tooltip";
// icons
import ExcelIcon from "mdi-material-ui/FileExcel";
import ArrowBack from "@material-ui/icons/ArrowBack";
//jazasoft
import { PageFooter, Button, RestMethods, FETCH_START, FETCH_END } from "jazasoft";
//local
import Report from "./Report";
import CardHeader from "../../../components/CardHeader";
import Table, { LinkField } from "../../../components/Table";
//helpers
import { toFixed, getDistinctValues } from "../../../utils/helpers";
import { dataProvider } from "../../../App";
import handleError from "../../../utils/handleError";
import { colorSecondary } from "../../../utils/colors";
import { getEchartOptions, DataType, getReportSerchText } from "../../../helpers/reportHelper";
import { PART_SHELL, MacroReportId } from "../../../constants";

const processOrderList = orderList => {
  return orderList
    .map(e => ({ ...e, ...e }))
    .filter(e => e.noOfLots === e.lot && e.part.includes(PART_SHELL))
    .map(e => ({
      ...e,
      month: moment(e.orderCloseDate).format("MMM, YY"),
      date: moment(e.orderCloseDate).format("ll"),
      cutPercent: e.orderQty > 0 ? `${toFixed((e.cumCutQty * 100) / e.orderQty, 2)}%` : `${0.0}%`
    }));
};

const columns1 = ({ onClick }) => {
  return [
    { dataKey: "month", title: "Month", element: <LinkField onClick={onClick} /> },
    { dataKey: "poCount", title: "Cut Orders" },
    { dataKey: "orderQty", title: "Order Qty" },
    { dataKey: "cutQty", title: "Cut Qty" },
    { dataKey: "cutPercentage", title: "Cut Percentage" }
  ];
};

const columns2 = [
  { dataKey: "date", title: "Date" },
  {
    dataKey: "poRef",
    title: "PO Ref",
    element: (
      <LinkField
        onClick={({ record }) => e =>
          record.orderId && window.open(`/reports/micro/reconciliation/${record.orderId}/view`, "_blank")}
      />
    )
  },
  { dataKey: "color", title: "Color" },
  { dataKey: "customer", title: "Customer" },
  { dataKey: "orderQty", title: "Order Qty" },
  { dataKey: "cutQty", title: "Cut Qty" },
  { dataKey: "cutPercent", title: "Cut Percentage" }
];

const getOptions = ({ monthlyCutAnalysis, orderList }) => {
  if (!monthlyCutAnalysis || Object.keys(monthlyCutAnalysis).length === 0) return {};
  return {
    color: [colorSecondary.main],
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow"
      }
    },
    xAxis: [
      {
        type: "category",
        data: Object.keys(monthlyCutAnalysis)
      }
    ],
    yAxis: [
      {
        type: "value",
        show: false,
        axisLabel: {
          formatter: "{value}%"
        }
      }
    ],
    series: [
      {
        name: "cutPercent",
        type: "bar",
        barWidth: 25,
        data: Object.keys(monthlyCutAnalysis).map(month => monthlyCutAnalysis[month].cutPercent),
        itemStyle: {
          normal: {
            color: colorSecondary.main,
            label: {
              show: true,
              position: "top",
              formatter: "{c}%"
            }
          }
        }
      }
    ]
  };
};

const styles = {
  root: {
    margin: "1.5em 0"
  },
  displayCenter: {
    display: "flex",
    justifyContent: "center"
  }
};

class CutAnalysisReportView extends Component {
  state = {
    page: 1,
    month: null,
    rollList: []
  };

  onDownload = () => {
    const { month } = this.state;
    let filter = { ...this.props.filter };
    if (month) {
      filter = {
        ...filter,
        start: moment(month, "MMM, YY")
          .startOf("month")
          .toDate()
          .getTime(),
        end: moment(month, "MMM, YY")
          .endOf("month")
          .toDate()
          .getTime()
      };
    }
    const search = getReportSerchText(filter || {});

    const options = {
      url: "reports/download",
      responseType: "arraybuffer",
      params: { action: "cutAnalysis", search }
    };

    this.props.dispatch({ type: FETCH_START });
    dataProvider(RestMethods.CUSTOM, undefined, options)
      .then(response => {
        if (response.status === 200) {
          const header = response.headers["content-disposition"];
          var filename = header ? header.match(/filename="(.+)"/)[1] : "CUT ANALYSIS.xlsx";

          let blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
        }
        this.props.dispatch({ type: FETCH_END });
      })
      .catch(err => {
        handleError(err, this.props.dispatch);
        this.props.dispatch({ type: FETCH_END });
      });
  };

  onBack = () => {
    if (this.state.page === 1) {
      this.props.history.goBack();
    } else if (this.state.page === 2) {
      this.setState({ page: this.state.page - 1, month: null });
    }
  };

  onClick = ({ column, record }) => e => {
    if (column.dataKey === "month") {
      this.setState({ page: this.state.page + 1, [column.dataKey]: record[column.dataKey] });
    }
  };

  render() {
    const { page, month } = this.state;
    const { classes, filter = {} } = this.props;

    const orderList = this.props.orderList.map(e => ({
      ...e,
      month: moment(e.orderCloseDate).format("MMM, YY")
    }));

    if (orderList.length === 0) {
      return (
        <div style={{ marginTop: "3em" }}>
          <Typography className={classes.displayCenter} color="primary" variant="subtitle1">
            Data not available
          </Typography>
        </div>
      );
    }

    let rows = [],
      options = {};
    if (page === 1) {
      let months = [];
      let curr = moment(filter.end);
      while (curr.valueOf() >= moment(filter.start).startOf("month").valueOf()) {
        months.unshift(curr.format("MMM, YY"));
        curr = curr.subtract(1, "months");
      }

      let monthlyCutAnalysis = {}; // month -> {[dataKey]: value}, where dataKey = month, noOfPos, ...
      months.forEach(month => {
        const list = processOrderList(orderList).filter(e => e.month === month);

        const orderQty = list.reduce((acc, el) => acc + el.orderQty, 0);
        const cutQty = list.reduce((acc, el) => acc + el.cumCutQty, 0);
        const cutPercent = orderQty > 0 ? toFixed((cutQty * 100) / orderQty, 1) : 0.0;

        const poCount = getDistinctValues(list.map(e => e.orderId)).length;

        monthlyCutAnalysis[month] = {
          month,
          poCount,
          orderQty,
          cutQty,
          cutPercent,
          cutPercentage: `${cutPercent}%`
        };
      });

      rows = [...months].reverse().map(month => monthlyCutAnalysis[month]);
      options = getOptions({ monthlyCutAnalysis });
    } else if (page === 2) {
      rows = processOrderList(orderList)
        .filter(e => e.month === month)
        .sort((a, b) => b.orderCloseDate - a.orderCloseDate)
        .map(e => ({ ...e, cutQty: `${e.cumCutQty} ${e.noOfLots > 1 ? "*" : ""}` }));
    }
    return (
      <div className={classes.root}>
        <div className={classes.content}>
          {page === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardHeader title="Monthly Cut Percent" />
                  <Divider />
                  <CardContent>
                    <ReactEchartsCore
                      echarts={echarts}
                      option={options}
                      style={{ height: "235px", width: "100%" }}
                      opts={{ renderer: "svg" }}
                      className="react_for_echarts"
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardHeader title="Cut Percent Deviation" />
                  <Divider />
                  <CardContent>
                    <ReactEchartsCore
                      echarts={echarts}
                      option={
                        getEchartOptions({ orderList, dataTypeList: [DataType.CUT_ANALYSIS] })[DataType.CUT_ANALYSIS]
                      }
                      style={{ height: "235px", width: "100%" }}
                      opts={{ renderer: "svg" }}
                      className="react_for_echarts"
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title="Monthly Cut Analysis" />
                  <Divider />
                  <CardContent style={{ padding: 0, overflowY: "auto" }}>
                    <Table columns={columns1({ onClick: this.onClick })} rows={rows} />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
          {page === 2 && (
            <Card>
              <CardHeader title={`Monthly Cut Analysis: ${month}`} />
              <Divider />
              <CardContent style={{ padding: 0, height: 350, overflowY: "auto" }}>
                <Table columns={columns2} rows={rows} />
              </CardContent>
            </Card>
          )}
        </div>

        <PageFooter className={classes.displayCenter}>
          <Button label="Back" variant="contained" onClick={this.onBack}>
            <ArrowBack />
          </Button>
          <Button
            label="Download"
            variant="contained"
            style={{ color: "green", marginLeft: "1em" }}
            onClick={this.onDownload}
          >
            <ExcelIcon />
          </Button>
        </PageFooter>
      </div>
    );
  }
}

const StyledCutAnalysisReport = withStyles(styles)(CutAnalysisReportView);

const CutAnalysisReport = props => (
  <Report title="Cut Analysis Report" defaultMonths={6} macroReportId={MacroReportId.CUT_QTY_ANALYSIS} {...props}>
    <StyledCutAnalysisReport {...props} />
  </Report>
);

export default CutAnalysisReport;
